import { frameId } from "../assets/manifest.ts";
import { decodeBase64, encodeBase64 } from "../document/base64.ts";
import { asRecord, asString } from "../document/validate.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type WorldDocument } from "../document/types.ts";
import { presenceArtCells } from "../editor/presence.ts";
import { presenceArea, type PresenceArea } from "../editor/presence-area.ts";
import type { AppearanceTable } from "./behaviour.ts";
import type { CompileContext } from "./context.ts";

/**
 * The sight planes of a compiled map (ADR-135 decision 2): for each authoring cell, which presence area (a
 * room) covers it and whether it lies deeper than the room's edge margin. The game's map server reads them to
 * leave out of a viewer's snapshot a character standing deep inside a room the viewer is not in. The planes are
 * rendered from the areas the page draws its roofs by (the painted cells of the prop's appearance, else the
 * ground under its art), so the editor, the page and the server read one rule, and every case the compile
 * cannot decide leaves cells at 0, which the server sends as it always has.
 */

/**
 * How deep inside a room's area, in whole tiles, a cell must lie to count as deep (ADR-135 decision 1.1): the
 * margin that keeps a character on the threshold, whose body stands in the open doorway, visible to someone
 * outside.
 */
export const SIGHT_ROOM_EDGE_TILES = 1;

/**
 * The most rooms one map numbers, the largest id a plane's byte holds.
 */
const MAX_ROOMS = 255;

/**
 * Written into the owner plane where two areas overlap, before the planes are cut down to bytes.
 */
const CONFLICT = 0xffff;

/**
 * A map's sight planes over its authoring cells, row-major, `width * COLLISION_CELLS_PER_TILE` cells wide:
 * `rooms` holds 0 for no room, for a cell two areas cover and for a room past the 255th, else the room's id
 * (1 to 255, in layer then prop order); `roomDeep` holds 1 where every cell within the edge margin, in all
 * eight directions, belongs to the same room.
 */
export interface MapSight {
  readonly rooms: Uint8Array;
  readonly roomDeep: Uint8Array;
}

/**
 * One server chunk's cut of the planes, `(chunkSize * COLLISION_CELLS_PER_TILE)^2` bytes each, at the authoring
 * cell like `ChunkZones`.
 */
export interface ChunkSight {
  readonly rooms: Uint8Array;
  readonly roomDeep: Uint8Array;
}

/**
 * The cells of a presence prop's area in its frame's space: those its appearance paints on its initial state,
 * mirrored with the prop, else the ground under its art from the measured frame the context holds.
 *
 * @param prop The placed prop, a presence prop of the map.
 * @param painted The activation cells of the prop's appearance in its initial state.
 * @param context The compile context, for the measured frames.
 * @param cellPixels Pixels per collision cell.
 * @returns The cells, empty when the area cannot be known: nothing painted and the frame not measured with art.
 */
function areaCells(prop: { readonly frame: { readonly tileset: string; readonly tile: number }; readonly flipX: boolean; readonly flipY: boolean }, painted: readonly (readonly [number, number])[], context: Pick<CompileContext, "presenceFrames">, cellPixels: number): readonly (readonly [number, number])[] {
  if (painted.length > 0) {
    return painted;
  }
  const size = context.presenceFrames?.[frameId(prop.frame.tileset, null, prop.frame.tile)];
  return size === undefined || size.art === undefined ? [] : presenceArtCells(size, cellPixels, prop.flipX, prop.flipY);
}

/**
 * The frames a compile needs measured (`CompileContext.presenceFrames`) to number every room of a world: the placed
 * frame of each presence prop that paints no area cells, whose area is the ground under its art. A build measures
 * these from the pixels before it compiles, since the atlas the page reads them from is built after.
 *
 * @param world The world, its props placed as the compile reads them.
 * @param context The compile context, whose object types say which props are driven by presence.
 * @returns The frame ids, each once, in first-met order.
 */
export function presenceFrameIds(world: WorldDocument, context: Pick<CompileContext, "objectTypes">): ReadonlySet<string> {
  const driven = new Set(context.objectTypes.filter((type) => type.inside !== undefined).map((type) => type.type));
  const ids = new Set<string>();
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        const painted = prop.behaviour?.activation;
        if (prop.behaviour !== undefined && driven.has(prop.behaviour.type) && !(Array.isArray(painted) && painted.length > 0)) {
          ids.add(frameId(prop.frame.tileset, null, prop.frame.tile));
        }
      }
    }
  }
  return ids;
}

/**
 * Marks areas into an owner plane in order: each area that marks a cell of the map takes the next id, up to
 * `MAX_ROOMS`, and a cell a second area covers becomes `CONFLICT`. An area past the last id is skipped and reported.
 *
 * @param owner The owner plane, all 0, `columns * rows` long.
 * @param areas The areas, in room order.
 * @param columns Cells across.
 * @param rows Cells down.
 * @param past Hears the index of each area that marks the map but is past the last id.
 * @returns How many rooms were numbered.
 */
function markOwners(owner: Uint16Array, areas: readonly PresenceArea[], columns: number, rows: number, past: (index: number) => void): number {
  let rooms = 0;
  areas.forEach((area, index) => {
    const marked: number[] = [];
    for (let y = Math.max(0, area.y0); y < Math.min(rows, area.y0 + area.height); y += 1) {
      for (let x = Math.max(0, area.x0); x < Math.min(columns, area.x0 + area.width); x += 1) {
        if (area.cells[(y - area.y0) * area.width + (x - area.x0)] === 1) {
          marked.push(y * columns + x);
        }
      }
    }
    if (marked.length === 0) {
      return;
    }
    if (rooms >= MAX_ROOMS) {
      past(index);
      return;
    }
    rooms += 1;
    for (const at of marked) {
      owner[at] = owner[at] === 0 ? rooms : CONFLICT;
    }
  });
  return rooms;
}

/**
 * The room plane of a set of roof areas, by the rule the compile writes the server's sight planes with
 * (`mapSight`): a room's id where one area covers a cell, and 0 where none does, where two overlap, and for an
 * area past the 255th. The page builds the same plane from the roof areas it draws, so it and the map server agree
 * cell for cell on who stands under a roof.
 *
 * @param areas The roof areas, in room order.
 * @param columns Cells across the map.
 * @param rows Cells down the map.
 * @returns The plane, one byte a cell.
 */
export function roomPlaneOf(areas: readonly PresenceArea[], columns: number, rows: number): Uint8Array {
  const owner = new Uint16Array(columns * rows);
  markOwners(owner, areas, columns, rows, () => undefined);
  return Uint8Array.from(owner, (value) => (value === CONFLICT ? 0 : value));
}

/**
 * Marks the cells every room covers on a map, the owner plane: a room's id where one area covers a cell and
 * `CONFLICT` where a second does. A room takes an id only once its area marks a cell of the map, so a prop whose
 * area lies wholly off the map takes none and counts toward no cap.
 *
 * @param world The world, for its tile size.
 * @param map The map.
 * @param context The compile context.
 * @param appearances The world's appearance table.
 * @param columns Cells across the map.
 * @param rows Cells down the map.
 * @param notes Receives a sentence for each roof left out because the map already numbers 255 rooms.
 * @returns The owner plane, or null when no room covers a cell of the map.
 */
function ownerPlane(world: WorldDocument, map: MapDocument, context: CompileContext, appearances: AppearanceTable, columns: number, rows: number, notes: string[]): Uint16Array | null {
  const cellPixels = world.tileSize / COLLISION_CELLS_PER_TILE;
  const owner = new Uint16Array(columns * rows);
  const areas: PresenceArea[] = [];
  const ids: string[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const index = appearances.indexOf.get(prop);
      const look = index === undefined ? undefined : appearances.appearances[index];
      if (look === undefined || look.inside === undefined) {
        continue;
      }
      const cells = areaCells(prop, look.states[look.initial]?.activation ?? [], context, cellPixels);
      if (cells.length === 0) {
        continue;
      }
      areas.push(presenceArea(prop, cells));
      ids.push(prop.id);
    }
  }
  const rooms = markOwners(owner, areas, columns, rows, (index) => notes.push(`map ${map.id}: roof ${ids[index] as string} is past the ${MAX_ROOMS} rooms a map numbers, so characters inside it are not hidden from outside viewers by the server`));
  return rooms === 0 ? null : owner;
}

/**
 * The cells whose whole neighbourhood within `edge` cells, the square around them, belongs to their own room.
 * A cell beside the map's rim or beside another room or an overlap is not deep. Run lengths along the rows and
 * then the columns make it linear in the cells of the map.
 *
 * @param rooms The room plane.
 * @param columns Cells across.
 * @param rows Cells down.
 * @param edge The margin in cells.
 * @returns The deep plane.
 */
function deepPlane(rooms: Uint8Array, columns: number, rows: number, edge: number): Uint8Array {
  const deep = new Uint8Array(rooms.length);
  const run = new Uint16Array(rooms.length);
  const spans = new Uint8Array(rooms.length);
  for (let y = 0; y < rows; y += 1) {
    const row = y * columns;
    let length = 0;
    for (let x = 0; x < columns; x += 1) {
      const id = rooms[row + x] as number;
      length = id === 0 ? 0 : x > 0 && rooms[row + x - 1] === id ? length + 1 : 1;
      run[row + x] = length;
    }
    length = 0;
    for (let x = columns - 1; x >= 0; x -= 1) {
      const id = rooms[row + x] as number;
      length = id === 0 ? 0 : x < columns - 1 && rooms[row + x + 1] === id ? length + 1 : 1;
      spans[row + x] = length > edge && (run[row + x] as number) > edge ? 1 : 0;
    }
  }
  for (let x = 0; x < columns; x += 1) {
    let length = 0;
    for (let y = 0; y < rows; y += 1) {
      const at = y * columns + x;
      length = spans[at] === 0 ? 0 : y > 0 && spans[at - columns] === 1 && rooms[at - columns] === rooms[at] ? length + 1 : 1;
      run[at] = length;
    }
    length = 0;
    for (let y = rows - 1; y >= 0; y -= 1) {
      const at = y * columns + x;
      length = spans[at] === 0 ? 0 : y < rows - 1 && spans[at + columns] === 1 && rooms[at + columns] === rooms[at] ? length + 1 : 1;
      deep[at] = length > edge && (run[at] as number) > edge ? 1 : 0;
    }
  }
  return deep;
}

/**
 * The sight planes of one map (ADR-135 decision 2): a room for every presence prop whose area is known, the
 * area its appearance paints or, with none painted, the ground under its art as the context's measured frames
 * give it. Where two areas overlap a cell is no room, and past the 255th room none is numbered. The result is
 * a function of the map, the context and the appearance table alone. A roof that is no room because the map
 * numbers too many, and cells two areas cover, are reported in `notes`.
 *
 * @param world The world, for its tile size.
 * @param map The map, its props placed as the compile reads them.
 * @param context The compile context; its `presenceFrames` size the roofs that paint no cells.
 * @param appearances The world's appearance table.
 * @param notes Receives a sentence for each roof or overlap the server will not hide anyone for.
 * @returns The planes, or null when the map holds no room.
 */
export function mapSight(world: WorldDocument, map: MapDocument, context: CompileContext, appearances: AppearanceTable, notes: string[] = []): MapSight | null {
  const columns = map.width * COLLISION_CELLS_PER_TILE;
  const rows = map.height * COLLISION_CELLS_PER_TILE;
  const owner = ownerPlane(world, map, context, appearances, columns, rows, notes);
  if (owner === null) {
    return null;
  }
  const overlapped = owner.reduce((count, value) => count + (value === CONFLICT ? 1 : 0), 0);
  if (overlapped > 0) {
    notes.push(`map ${map.id}: roof areas overlap on ${overlapped} cells, which are no room, so the server hides no one standing there`);
  }
  const rooms = Uint8Array.from(owner, (value) => (value === CONFLICT ? 0 : value));
  if (!rooms.some((value) => value !== 0)) {
    return null;
  }
  return { rooms, roomDeep: deepPlane(rooms, columns, rows, SIGHT_ROOM_EDGE_TILES * COLLISION_CELLS_PER_TILE) };
}

/**
 * One chunk's cut of a map's sight planes, padded with 0 beyond the map.
 *
 * @param sight The map's planes.
 * @param map The map, for its size.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The chunk's planes, or null when no cell of the chunk is in a room.
 */
export function chunkSight(sight: MapSight, map: MapDocument, cx: number, cy: number, chunkSize: number): ChunkSight | null {
  const side = chunkSize * COLLISION_CELLS_PER_TILE;
  const columns = map.width * COLLISION_CELLS_PER_TILE;
  const rows = map.height * COLLISION_CELLS_PER_TILE;
  const rooms = new Uint8Array(side * side);
  const roomDeep = new Uint8Array(side * side);
  let held = false;
  for (let y = 0; y < side && cy * side + y < rows; y += 1) {
    const from = (cy * side + y) * columns + cx * side;
    const width = Math.min(side, columns - cx * side);
    const row = sight.rooms.subarray(from, from + width);
    rooms.set(row, y * side);
    roomDeep.set(sight.roomDeep.subarray(from, from + width), y * side);
    held = held || row.some((value) => value !== 0);
  }
  return held ? { rooms, roomDeep } : null;
}

/**
 * A chunk's sight planes as the plain JSON the canonical chunk text holds: both planes as base64 of their bytes.
 *
 * @param sight The chunk's planes.
 * @returns The plain object, in fixed key order.
 */
export function plainChunkSight(sight: ChunkSight): { readonly rooms: string; readonly roomDeep: string } {
  return { rooms: encodeBase64(sight.rooms), roomDeep: encodeBase64(sight.roomDeep) };
}

/**
 * Reads a chunk's sight planes: two planes of one byte per authoring cell, the deep plane holding 0 or 1 and
 * only on a cell some room covers.
 *
 * @param value The `sight` value.
 * @param authoringCells The chunk's cells at the authoring cell.
 * @returns The planes.
 * @throws Error naming the path of the first fault.
 */
export function readChunkSight(value: unknown, authoringCells: number): ChunkSight {
  const record = asRecord(value, "sight");
  const rooms = decodeBase64(asString(record["rooms"], "sight.rooms"));
  if (rooms.length !== authoringCells) {
    throw new Error(`sight.rooms has ${rooms.length} cells, expected ${authoringCells}`);
  }
  const roomDeep = decodeBase64(asString(record["roomDeep"], "sight.roomDeep"));
  if (roomDeep.length !== authoringCells) {
    throw new Error(`sight.roomDeep has ${roomDeep.length} cells, expected ${authoringCells}`);
  }
  for (let i = 0; i < roomDeep.length; i += 1) {
    const flag = roomDeep[i] as number;
    if (flag > 1 || (flag === 1 && rooms[i] === 0)) {
      throw new Error(`sight.roomDeep[${i}] holds ${flag}, which is neither 0 nor 1 on a cell in a room`);
    }
  }
  return { rooms, roomDeep };
}
